

































import { Component, Vue } from "vue-property-decorator";
import { CrudService } from "../core/services/crudService";
import { Settings, SettingsService } from "../core/services/configService";
import { CustomerHelper } from "../crm/helpers/customerHelper";
import { ProgramPackTexts } from "../bookings/programPackTexts";
import { GuidelineApi } from "../components/guidelines/guidelineApi";
import { TreatmentApi } from "../components/treatments/treatmentApi";
import ProgramPage from "../components/programs/ProgramPage.vue";
import ProgramPackHead from "../components/programs/ProgramPackHead.vue";
import GuidelineRow from "../components/guidelines/GuidelineRow.vue";
import CzCard from "../components/cz/CzCard.vue";
import CzLink from "../components/cz/CzLink.vue";
import CzNotice from "../components/cz/CzNotice.vue";
import CzPlanRow from "../components/cz/CzPlanRow.vue";
import CzScroller from "../components/cz/CzScroller.vue";
import CzSection from "../components/cz/CzSection.vue";

Component.registerHooks(["beforeRouteEnter", "beforeRouteLeave"]);

const PendingPayment = 1;
const Active = 2;
const LinesShown = 3;

// Steps of booking or paying and the pages opened from here are never where Back should take the patient.
const FlowPages = ["/my-plan", "/bookings", "/checkout", "/cz/", "/customer-programs/", "/programs/", "/guidelines/", "/treatment"];
const OpenedFromHere = ["/customer-programs/", "/guidelines/", "/treatment"];

let scrollWhenLeft = 0;

// The index of what the patient has been prescribed: each program, the treatment and every guideline once, wherever it
// came from. Each one opens on its own page and coming back lands on the same spot.
@Component({
  components: { ProgramPage, ProgramPackHead, GuidelineRow, CzCard, CzLink, CzNotice, CzPlanRow, CzScroller, CzSection },
})
export default class MyPlanPage extends Vue {
  programs: any[] = [];
  routines: any[] = [];
  lines: any[] = [];
  loaded: boolean = false;
  failed: boolean = false;
  treatmentFailed: boolean = false;
  cameFrom: string = "";
  returning: boolean = false;

  beforeRouteEnter(to: any, from: any, next: any) {
    next((vm: any) => {
      if (from.name && !FlowPages.some((p) => from.path.startsWith(p))) vm.cameFrom = from.fullPath;
      vm.returning = OpenedFromHere.some((p) => from.path.startsWith(p));
    });
  }

  beforeRouteLeave(to: any, from: any, next: any) {
    scrollWhenLeft = window.scrollY;
    next();
  }

  get backTo(): string {
    return (this.$route.query.back as string) || this.cameFrom || "/bookingtypes";
  }

  get backLabel(): string {
    const to = this.backTo;
    if (to.startsWith("/today")) return this.$t("Today").toString();
    if (to.startsWith("/bookingtypes")) return this.$t("Reserve").toString();
    return this.$t("Back").toString();
  }

  get wholeTreatment(): any {
    return { path: "/treatment", query: { tab: "all" } };
  }

  get firstLines(): any[] {
    return this.lines.slice(0, LinesShown);
  }

  get guidelineRows(): { [id: number]: any } {
    const byId: { [id: number]: any } = {};
    this.routines.forEach((r: any) => (byId[r.id] = r));
    return byId;
  }

  get emptyText(): string {
    const nothing = !this.programs.length && !this.routines.length && !this.lines.length;
    return this.loaded && !this.failed && !this.treatmentFailed && nothing ? this.$t("You have no plan in progress").toString() : "";
  }

  // A line not in force says so: what starts next month never reads as what to take now.
  lineSub(l: any): string {
    const date = (d: string) => ProgramPackTexts.date(d, false);
    if (l.state === "future") return [this.$t("Starts on {0}").toString().replace("{0}", date(l.startDate)), l.when].filter((x) => x).join(" · ");
    if (l.state === "ended") return this.$t("Ended on {0}").toString().replace("{0}", date(l.lastDay));
    return l.when;
  }

  // The programs in progress first; the finished ones stay at the end so the patient keeps them.
  inProgressFirst(programs: any[]): any[] {
    const current = programs.filter((e: any) => e.status === PendingPayment || e.status === Active);
    return current.concat(programs.filter((e: any) => current.indexOf(e) < 0));
  }

  mounted() {
    return this.load();
  }

  // A failed read never looks like an empty plan: it says so and offers to retry.
  async load() {
    const [programs, routines, treatment] = await Promise.all([
      new CrudService().httpGet(`${Settings.HostName}/api/publicprogrampacks/customer/${CustomerHelper.getStoredCustomer()}`).catch(() => null),
      GuidelineApi.list().catch(() => null),
      TreatmentApi.detail().catch(() => null),
    ]);
    const programsOk = !!programs && programs.ok === true;
    const routinesOk = !!routines && routines.ok === true;
    this.treatmentFailed = !treatment || treatment.ok !== true;
    this.failed = !programsOk || !routinesOk;
    if (programsOk) this.programs = this.inProgressFirst(programs.data);
    if (routinesOk) this.routines = routines.data;
    if (!this.treatmentFailed) this.lines = treatment.model.lines;
    this.loaded = true;
    new SettingsService().setPageTitle(this.$t("My plan").toString());
    if (this.returning) this.$nextTick(() => window.scrollTo(0, scrollWhenLeft));
    this.returning = false;
  }
}
